import { Form, FormInstance } from 'antd';
import { useMemo } from 'react';

import { CarrierDTO, ShipmentClassification } from '@invenco/shipping-interface';

import { shipmentClassificationTitle } from '../../../../../shared/title-maps';
import { ComponentData } from '../../../../../shared/types';

type Props = {
  carriers: CarrierDTO[];
  existingCarrierClassifications: Array<{
    carrierId: string;
    classification: ShipmentClassification | null;
  }>;
  onSave: (carrier: CarrierDTO, classification: ShipmentClassification | null) => void;
  onClose: () => void;
};

type Models = {
  form: FormInstance;
  classificationOptions: Array<{ label: string; value: ShipmentClassification }>;
  hasExistingUnclassified: boolean;
};

type Operations = {
  filter: (inputValue: string, option: any) => boolean;
  submit: (data: Record<string, any>) => void;
  onCarrierChange: () => void;
};

export function useCarrierSelectionModal({
  carriers,
  existingCarrierClassifications,
  onSave,
  onClose,
}: Props): ComponentData<Models, Operations> {
  const [form] = Form.useForm();
  const selectedCarrierId: string | undefined = Form.useWatch('carrierId', form);

  const existingForCarrier = useMemo(
    () =>
      new Set(
        existingCarrierClassifications
          .filter(({ carrierId }) => carrierId === selectedCarrierId)
          .map(({ classification }) => classification),
      ),
    [selectedCarrierId, existingCarrierClassifications],
  );

  const classificationOptions = useMemo(
    () =>
      Object.values(ShipmentClassification)
        .filter((c) => !existingForCarrier.has(c))
        .map((c) => ({ label: shipmentClassificationTitle[c], value: c })),
    [existingForCarrier],
  );

  const hasExistingUnclassified = existingForCarrier.has(null);

  const onCarrierChange = () => {
    form.resetFields(['classification']);
  };

  const filter = (inputValue: string, option: { label?: string }) =>
    (option.label?.toString() ?? '').toLocaleLowerCase().includes(inputValue.toLocaleLowerCase());

  const submit = (data: Record<string, any>) => {
    const carrier = carriers.find(({ id }) => id === data.carrierId);
    if (carrier) onSave(carrier, data.classification ?? null);
    form.resetFields();
    onClose();
  };

  return {
    models: {
      form,
      classificationOptions,
      hasExistingUnclassified,
    },
    operations: {
      filter,
      submit,
      onCarrierChange,
    },
  };
}
